import { CSSObject } from '@emotion/react'
import changeCase from 'change-case'
import { FC, useCallback, useEffect, useState } from 'react'
import { HttpClient } from '@opallabs/api'
import { Asset, kuiperClassNames, TextArea } from '@opallabs/kuiper'
import { Icon } from '@opallabs/lapidary'
import {
  LinkWithData,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
} from '../Content/helpers/types'
import { LinkPreviewField, defaultFields } from './fields'
import { LinkPreviewSettings } from './LinkPreviewSettings'
import * as styles from './styles'

type LinkPreviewAssetCarouselProps = {
  assetStyle?: CSSObject
  descriptionStyle?: CSSObject
  disableRemoveLink?: boolean
  domainStyle?: CSSObject
  fields?: LinkPreviewField[]
  hasRemovedLink?: () => void
  hasSquareInlineAsset?: boolean
  link: string
  linkStyle?: CSSObject
  linkWithData?: LinkWithData
  onLinkDataUpdate?: OnLinkDataUpdate
  onShowAssetPicker?: (props: OnShowAssetPickerProps) => void
  readOnly?: boolean
  style?: CSSObject
  titleStyle?: CSSObject
}

export const LinkPreviewAssetCarousel: FC<LinkPreviewAssetCarouselProps> = ({
  assetStyle,
  descriptionStyle,
  disableRemoveLink = false,
  domainStyle,
  fields = defaultFields,
  hasRemovedLink,
  hasSquareInlineAsset,
  link,
  linkStyle,
  linkWithData,
  onLinkDataUpdate,
  onShowAssetPicker,
  readOnly = false,
  style,
  titleStyle,
}) => {
  const [isTooltipActive, setIsTooltipActive] = useState(false)
  const [currentAsset, setCurrentAsset] = useState(0)

  const assets = linkWithData?.assetReferences || []
  const title = linkWithData?.title || ''
  const domain = linkWithData?.domain || ''
  const description = linkWithData?.description || ''
  const useInline =
    assets.length > 0 && assets[0].format === 'ico' && !!hasSquareInlineAsset

  const props = { title, domain, description }
  const propStyles = { titleStyle, domainStyle, descriptionStyle }

  // Extracts a link preview and assigns to post's linksWithData attribute
  const onRefetchLinkPreview = useCallback(() => {
    HttpClient()
      .post(`/links/extract_preview?url=${encodeURIComponent(link)}`)
      .then(({ data: htmlResponse }) => {
        const newDescription = htmlResponse.description || ''
        const newTitle = htmlResponse.title || ''
        const newDomain = htmlResponse.provider_display
          ? htmlResponse.provider_display.replace(/^www\./, '')
          : ''
        const newAssets = htmlResponse.images || []
        if (newDescription && newDomain && newTitle) {
          const dataModel = {
            asset_references: newAssets,
            description: newDescription,
            domain: newDomain,
            title: newTitle,
            url: link,
          }
          onLinkDataUpdate?.(dataModel, 'url', link)
        }
      })
      .catch((err) => {
        console.error(err)
      })
  }, [link, onLinkDataUpdate])

  useEffect(() => {
    if (!linkWithData) {
      onRefetchLinkPreview()
    }
  }, [linkWithData, onRefetchLinkPreview])

  // Removes the link preview image at the currentAsset index
  const onRemoveImage = () => {
    const newAssets = [...assets]
    newAssets.splice(currentAsset, 1)
    onLinkDataUpdate?.(newAssets, 'asset_references', linkWithData?.url || '')
    onGoBack()
  }

  // Removes the link preview by nullifying the linkWithData attribute
  const onRemoveLinkPreview = () => {
    onLinkDataUpdate?.(null, 'reset', linkWithData?.url || '')
    hasRemovedLink?.()
    setCurrentAsset(0)
  }

  const handleShowAssetPicker = () => {
    onShowAssetPicker?.({
      destination: 'links_with_data',
      url: linkWithData?.url || '',
    })
  }

  // Scrolls backward through link preview asset carouselx
  const onGoBack = () => {
    const newCurrentAsset = currentAsset - 1
    if (newCurrentAsset === -1) return
    setCurrentAsset(newCurrentAsset)
  }

  // Scrolls forward through link preview asset carousel
  const onGoForward = () => {
    const newCurrentAsset = currentAsset + 1
    if (newCurrentAsset >= assets.length) return
    setCurrentAsset(newCurrentAsset)
  }

  const onPreviewFieldChange = (val: string, field: LinkPreviewField) => {
    if (!linkWithData) {
      const defaults = {
        url: link,
        title: '',
        domain: '',
        description: '',
        asset_references: [],
      }
      onLinkDataUpdate?.({ ...defaults, [field.field]: val }, 'url', link)
    } else {
      onLinkDataUpdate?.(val, field.field, linkWithData?.url)
    }
  }

  return (
    <div css={[style, styles.baseStyle({ useInline })]}>
      <div css={styles.previewWrapper(isTooltipActive)}>
        {assets.length > 0 && (
          <div className={kuiperClassNames('preview-wrapper')}>
            <Asset
              imageFit="cover"
              hasAssetOverlay={false}
              assetReference={assets[currentAsset]}
              overrideStyles={{
                assetBase: [assetStyle, styles.assetStyle],
              }}
              placeholder={false}
              removeable={false}
            />
            <div
              css={styles.settingsIconWrapper(readOnly)}
              className={kuiperClassNames('settings-icon-wrapper')}
            >
              {!readOnly && (
                <ul css={styles.arrowsWrapper}>
                  <li>
                    <button
                      onClick={onGoBack}
                      type="button"
                      aria-label="Go to Previous Asset"
                      disabled={currentAsset === 0}
                    >
                      <Icon
                        glyph="arrow-w"
                        useHypernova
                        ariaHidden
                        width={12}
                        height={12}
                      />
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={onGoForward}
                      type="button"
                      aria-label="Go to Next Asset"
                      disabled={currentAsset === assets.length - 1}
                    >
                      <Icon
                        glyph="arrow-e"
                        useHypernova
                        ariaHidden
                        width={12}
                        height={12}
                      />
                    </button>
                  </li>
                </ul>
              )}
              <LinkPreviewSettings
                open={isTooltipActive}
                onOpenChange={setIsTooltipActive}
                asset={assets[currentAsset]}
                link={link}
                disableRemoveLink={disableRemoveLink}
                onRefetchLinkPreview={onRefetchLinkPreview}
                onRemoveImage={onRemoveImage}
                onRemoveLinkPreview={onRemoveLinkPreview}
                onShowAssetPicker={handleShowAssetPicker}
                readOnly={readOnly}
              />
            </div>
          </div>
        )}
      </div>
      <div
        className={kuiperClassNames('link-preview-asset-carousel-inputs')}
        css={[linkStyle, styles.linkStyle(assets.length === 0)]}
      >
        {fields &&
          fields.map((field) => (
            <div key={field.field} css={styles.inlineIcon(field.icon !== '')}>
              {field.icon && (
                <div css={styles.fieldIcon}>
                  <Icon
                    glyph={field.icon}
                    useHypernova
                    ariaHidden
                    width={14}
                    height={14}
                  />
                </div>
              )}
              <TextArea
                ariaLabel={`Link Preview ${changeCase.title(field.field)} Text`}
                minRows={1}
                readOnly={readOnly}
                placeholder={field.placeholder}
                value={props[field.field]}
                style={propStyles[field.style]}
                onChange={(val: string) => onPreviewFieldChange(val, field)}
              />
            </div>
          ))}
      </div>
      {assets.length === 0 && (
        <div css={[linkStyle, styles.footerBarStyle]}>
          <div css={styles.footerSettingsIconWrapper(isTooltipActive)}>
            <LinkPreviewSettings
              open={isTooltipActive}
              onOpenChange={setIsTooltipActive}
              link={link}
              disableRemoveLink={disableRemoveLink}
              onRefetchLinkPreview={onRefetchLinkPreview}
              onRemoveImage={onRemoveImage}
              onRemoveLinkPreview={onRemoveLinkPreview}
              onShowAssetPicker={handleShowAssetPicker}
            />
          </div>
        </div>
      )}
    </div>
  )
}
